// Tax Savings Packet (Philip 2026-10-30, docs/tax-savings-packet-plan.md): the documents filed for a property and its
// ticked parcels in the value year, sorted into packet sections, with the starting ticks. Pure — the page fetches.
//
// ⛔ THE TICKS ARE ONLY A STARTING POINT. Every mergeable document can be ticked or unticked, several or none per
// section. Only the top sheet is fixed, and only a file that cannot be merged (Excel, Word, email) cannot be ticked.

export const PACKET_SECTIONS = [
  { key: 'top', label: 'Top sheet' },
  { key: 'reduction', label: 'Reduction support' },
  { key: 'starting', label: 'Starting value' },
  { key: 'bill', label: 'Tax bill' },
  { key: 'other', label: 'Other documents' },
];

const REDUCTION = new Set(['notice_of_decision', 'settlement_agreement', 'appeal_recommendations', 'appeal_correspondence']);
const STARTING = new Set(['assessment_notice', 'record_card', 'supplemental_notice']);
const BILL = new Set(['tax_bill', 'supplemental_tax_bill']);
// Intake piles: not filed documents, never offered.
export const PILE_FOLDERS = new Set(['client_uploads', 'need_to_review', 'task_attachments']);

// A document uploaded at an appeal level (the parcel card's level upload) is reduction support whatever its type.
export function sectionOf(doc) {
  if (doc.appeal_level || REDUCTION.has(doc.type_key)) return 'reduction';
  if (STARTING.has(doc.type_key)) return 'starting';
  if (BILL.has(doc.type_key)) return 'bill';
  return 'other';
}

const ext = (name) => (String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || '';
export const fileKind = (name) => {
  const e = ext(name);
  return e === 'pdf' ? 'pdf' : e === 'jpg' || e === 'jpeg' ? 'jpg' : e === 'png' ? 'png' : null;
};

const filedAt = (d) => d.filed_at || d.created_at || '';
const byNumber = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });

/**
 * @param docs        file_exchange rows for the property and the parcels (value year and the year before)
 * @param parcels     the ticked report rows: { parcel_id, parcel (number), levelKey (the level the row is read at) }
 * @param year        the value year
 * @param billDocIds  Set of file_exchange ids the year's (and prior year's) current tax_bills were scanned from
 * → items in packet order: { id, doc, section, parcelId, parcelLabel, checked, mergeable, note }
 */
export function packetItems({ docs, parcels, year, billDocIds = new Set() }) {
  const y = String(year), prior = String(Number(year) - 1);
  const label = new Map(parcels.map((p) => [p.parcel_id, p.parcel]));
  const levelOf = new Map(parcels.map((p) => [p.parcel_id, p.levelKey || null]));
  const ours = (docs || []).filter((d) => !PILE_FOLDERS.has(d.folder_key) && (!d.parcel_id || label.has(d.parcel_id)));
  const thisYear = ours.filter((d) => String(d.year_subfolder) === y);

  const items = thisYear.map((d) => ({ doc: d, section: sectionOf(d), prior: false }));
  // A parcel (or the property, for property-level bills) with no bill this year gets its prior year's, flagged.
  const owners = [null, ...parcels.map((p) => p.parcel_id)];
  owners.forEach((owner) => {
    const has = items.some((i) => i.section === 'bill' && (i.doc.parcel_id || null) === owner);
    if (has) return;
    ours.filter((d) => String(d.year_subfolder) === prior && BILL.has(d.type_key) && !d.appeal_level && (d.parcel_id || null) === owner)
      .forEach((d) => items.push({ doc: d, section: 'bill', prior: true }));
  });

  // Starting ticks, per owner (a parcel, or the property).
  const firstNotice = new Map();
  const billPick = new Map();
  owners.forEach((owner) => {
    const mine = (s, f = () => true) => items.filter((i) => i.section === s && (i.doc.parcel_id || null) === owner && f(i)).map((i) => i.doc);
    // The first-filed Assessment Notice; with none, the first-filed Record Card (Midway 2021 has only a card).
    const first = (type) => mine('starting', (i) => i.doc.type_key === type).sort((a, b) => filedAt(a).localeCompare(filedAt(b)))[0];
    const notice = first('assessment_notice') || first('record_card');
    if (notice) firstNotice.set(owner, notice.id);
    const bills = mine('bill');
    const scanned = bills.filter((d) => billDocIds.has(d.id));
    billPick.set(owner, new Set((scanned.length ? scanned : bills.sort((a, b) => filedAt(b).localeCompare(filedAt(a))).slice(0, 1)).map((d) => d.id)));
  });
  const startTick = ({ doc, section }) => {
    const owner = doc.parcel_id || null;
    if (section === 'reduction') {
      if (doc.appeal_level) return doc.appeal_level === levelOf.get(owner);   // the level the report reads the parcel at
      return doc.type_key === 'notice_of_decision' || doc.type_key === 'settlement_agreement';
    }
    if (section === 'starting') return firstNotice.get(owner) === doc.id;
    if (section === 'bill') return !!billPick.get(owner)?.has(doc.id);
    return false;
  };

  const order = new Map(PACKET_SECTIONS.map((s, i) => [s.key, i]));
  return items.map((i) => {
    const mergeable = !!fileKind(i.doc.file_name);
    return {
      id: i.doc.id, doc: i.doc, section: i.section,
      parcelId: i.doc.parcel_id || null,
      parcelLabel: i.doc.parcel_id ? label.get(i.doc.parcel_id) : 'Property',
      checked: mergeable && startTick(i),
      mergeable,
      note: [i.prior ? 'prior year' : '', mergeable ? '' : 'can’t be merged'].filter(Boolean).join(' · '),
    };
  }).sort((a, b) => order.get(a.section) - order.get(b.section)
    || (a.parcelId ? 1 : 0) - (b.parcelId ? 1 : 0) || byNumber(a.parcelLabel, b.parcelLabel)
    || filedAt(a.doc).localeCompare(filedAt(b.doc)));
}
